/**
 * TechnicianFilterPopover — toolbar control to filter the route board by
 * technician. Defaults to "All". Selection is represented as `null` (= All) or
 * an explicit list of technician ids.
 */
import React from 'react';
import { Users, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CONTROL, activeCls, SECTION_LABEL, ValuePill } from '@/components/ui/control-deck';
import { cn } from '@/lib/utils';

export interface TechnicianFilterOption {
  id: string;
  name: string;
  color: string;
}

interface TechnicianFilterPopoverProps {
  technicians: TechnicianFilterOption[];
  /** null = All selected; otherwise the explicit set of selected ids. */
  selectedIds: string[] | null;
  onChange: (selectedIds: string[] | null) => void;
}

export const TechnicianFilterPopover: React.FC<TechnicianFilterPopoverProps> = ({
  technicians,
  selectedIds,
  onChange
}) => {
  const isAll = selectedIds === null;
  const selectedSet = new Set(selectedIds ?? technicians.map(t => t.id));

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    // If everything is selected, normalize back to "All" (null)
    if (next.size === technicians.length) {
      onChange(null);
    } else {
      onChange(Array.from(next));
    }
  };

  let valueText: string;
  if (isAll) {
    valueText = 'All';
  } else if (selectedSet.size === 0) {
    valueText = 'None';
  } else if (selectedSet.size === 1) {
    const onlyId = Array.from(selectedSet)[0];
    valueText = technicians.find(t => t.id === onlyId)?.name ?? '1';
  } else {
    valueText = `${selectedSet.size} selected`;
  }

  return (
    <div className='flex items-center gap-3'>
      <div className={SECTION_LABEL}>
        <Users className='h-4 w-4' />
        <span>Technicians</span>
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className={cn('h-8 text-xs gap-1.5', CONTROL, activeCls(!isAll))}
          >
            <span className='!flex items-center gap-1.5 truncate'>
              <ValuePill value={valueText} />
            </span>
            <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-64 p-2'>
          <div className='mb-1 flex items-center justify-between px-1'>
            <span className='text-xs font-semibold text-muted-foreground'>Technicians</span>
            <Button
              variant='ghost'
              size='sm'
              className='h-6 px-2 text-[11px] text-brand-primary-600 hover:text-brand-primary-700'
              onClick={() => onChange(null)}
              disabled={isAll}
            >
              Select all
            </Button>
          </div>
          <ScrollArea className='max-h-64'>
            <div className='space-y-0.5'>
              {technicians.map(tech => {
                const checked = selectedSet.has(tech.id);
                return (
                  <button
                    key={tech.id}
                    type='button'
                    onClick={() => toggle(tech.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                        checked ? 'border-transparent' : 'border-muted-foreground/40'
                      )}
                      style={checked ? { backgroundColor: tech.color } : undefined}
                    >
                      {checked && <Check className='h-3 w-3 text-white' />}
                    </span>
                    <span
                      className='h-2.5 w-2.5 flex-shrink-0 rounded-full'
                      style={{ backgroundColor: tech.color }}
                      aria-hidden
                    />
                    <span className='min-w-0 flex-1 truncate'>{tech.name}</span>
                  </button>
                );
              })}
              {technicians.length === 0 && (
                <p className='px-2 py-3 text-center text-xs text-muted-foreground'>
                  No technicians with routes today.
                </p>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TechnicianFilterPopover;
